/**
 * 路由兼容层
 * 提供与 vike 类似的 API，基于 react-router-dom 实现
 */
import { useNavigate as useRRNavigate, useLocation, useParams } from 'react-router-dom';

/**
 * 导航函数类型
 */
type NavigateOptions = {
  /** 替换当前历史记录（相当于 vike 的 overwriteLastHistoryEntry） */
  replace?: boolean;
  /** vike 兼容：替换当前历史记录 */
  overwriteLastHistoryEntry?: boolean;
};

/**
 * 页面上下文类型（兼容 vike 的 usePageContext）
 */
export interface PageContext {
  /** 当前 URL 路径 */
  urlPathname: string;
  /** 路由参数 */
  routeParams: Record<string, string | undefined>;
  /** URL 解析结果 */
  urlParsed: {
    search: Record<string, string>;
    pathname: string;
  };
  /** 页面数据（SPA 模式下为空） */
  data?: Record<string, unknown>;
}

/**
 * 导航函数（兼容 vike 的 navigate）
 */
export function useNavigate() {
  const rrNavigate = useRRNavigate();

  return (to: string, options?: NavigateOptions) => {
    const replace = options?.replace || options?.overwriteLastHistoryEntry || false;
    rrNavigate(to, { replace });
  };
}

/**
 * 导航函数（直接导出，兼容 vike/client/router 的 navigate）
 * 注意：这是一个需要在组件内使用的函数，返回实际的导航函数
 */
let globalNavigate: ((to: string, options?: NavigateOptions) => void) | null = null;

export function setGlobalNavigate(nav: (to: string, options?: NavigateOptions) => void) {
  globalNavigate = nav;
}

export function navigate(to: string, options?: NavigateOptions) {
  if (globalNavigate) {
    globalNavigate(to, options);
  } else {
    // 降级方案：直接使用 window.location
    if (options?.replace || options?.overwriteLastHistoryEntry) {
      window.location.replace(to);
    } else {
      window.location.href = to;
    }
  }
}

/**
 * 页面上下文 Hook（兼容 vike-react 的 usePageContext）
 */
export function usePageContext(): PageContext {
  const location = useLocation();
  const params = useParams();

  // 解析 URL 查询参数
  const searchParams = new URLSearchParams(location.search);
  const search: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    search[key] = value;
  });

  return {
    urlPathname: location.pathname,
    routeParams: params as Record<string, string | undefined>,
    urlParsed: {
      search,
      pathname: location.pathname,
    },
    data: {},
  };
}
